import React, { useEffect, useState } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Link,
    InputAdornment,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Backdrop,
    CircularProgress
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Email,
    Lock,
    Person,
    Phone,
    Business,
    CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
    validateEmail,
    validatePasswordStrong,
    validateName,
    validateConfirmPassword,
    isFormValid,
    getFirstErrorField,
    validateMobileNumber,
    decryptResponse,
    encryptResponse,
} from '../../services';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { setToken, setUserDetails } from '../../features/auth/authSlice';
import { useDispatch } from 'react-redux';
import OtpInput from '../../components/common/OtpInput';

const Signup: React.FC = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        organizationName: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        organizationName: '',
        password: '',
        confirmPassword: ''
    });
    const [touched, setTouched] = useState({
        firstName: false,
        lastName: false,
        email: false,
        mobile: false,
        organizationName: false,
        password: false,
        confirmPassword: false,
        department: false,
        departmentName: false
    });
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [onValueChange, setOnValueChange] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState('');
    const [department, setDepartment] = useState('IT');
    const [departmentError, setDepartmentError] = useState('');
    const [departmentName, setDepartmentName] = useState('');
    const [departmentNameError, setDepartmentNameError] = useState('');
    const [emailVerified, setEmailVerified] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState('');

    useEffect(() => {
        if (formData.email) {
            setEmailVerified(false);
            setOtpSent(false);
            setOtp('');
            setOtpError('');
        }
    }, [formData.email]);

    const validateFieldLocal = (name: string, value: string): string => {
        switch (name) {
            case 'firstName':
                return validateName(value, 'First name');
            case 'lastName':
                return validateName(value, 'Last name');
            case 'email':
                return validateEmail(value);
            case 'mobile':
                return validateMobileNumber(value);
            case 'organizationName':
                return validateName(value, 'Organization name');
            case 'password':
                return validatePasswordStrong(value);
            case 'confirmPassword':
                return validateConfirmPassword(formData.password, value);
            default:
                return '';
        }
    };

    const validateDepartment = (value: string): string => {
        return value ? '' : 'Department is required';
    };

    const validateDepartmentName = (value: string): string => {
        return value.trim() ? '' : 'Department Name is required';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOtpSent(false);
        const { name, value } = e.target;
        setOnValueChange(true);
        setErrorMessage('');
        setFormData({
            ...formData,
            [name]: value
        });

        if (touched[name as keyof typeof touched]) {
            const error = validateFieldLocal(name, value);
            setErrors({
                ...errors,
                [name]: error
            });

            if (name === 'password' && touched.confirmPassword) {
                const confirmPasswordError = validateConfirmPassword(value, formData.confirmPassword);
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: confirmPasswordError
                }));
            }
        }
    };

    const handleDepartmentChange = (e: React.ChangeEvent<{ value: unknown }>) => {
        const value = e.target.value as string;
        setDepartment(value);
        setDepartmentError(validateDepartment(value));

        if (value !== 'Others') {
            setDepartmentName('');
            setDepartmentNameError('');
        }
    };

    const handleDepartmentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setDepartmentName(value);
        if (touched.departmentName) {
            setDepartmentNameError(validateDepartmentName(value));
        }
    };

    const handleDepartmentNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setTouched({
            ...touched,
            departmentName: true
        });
        setDepartmentNameError(validateDepartmentName(value));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!onValueChange) return;
        const { name, value } = e.target;
        setTouched({
            ...touched,
            [name]: true
        });

        const error = validateFieldLocal(name, value);
        setErrors({
            ...errors,
            [name]: error
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({
            firstName: true,
            lastName: true,
            email: true,
            mobile: true,
            organizationName: true,
            password: true,
            confirmPassword: true,
            department: true,
            departmentName: department === 'Others'
        });
        const newErrors = {
            firstName: validateFieldLocal('firstName', formData.firstName),
            lastName: validateFieldLocal('lastName', formData.lastName),
            email: validateFieldLocal('email', formData.email),
            mobile: validateFieldLocal('mobile', formData.mobile),
            organizationName: validateFieldLocal('organizationName', formData.organizationName),
            password: validateFieldLocal('password', formData.password),
            confirmPassword: validateFieldLocal('confirmPassword', formData.confirmPassword)
        };

        const departmentError = validateDepartment(department);
        const departmentNameError = department === 'Others' ? validateDepartmentName(departmentName) : '';

        setDepartmentError(departmentError);
        setDepartmentNameError(departmentNameError);

        setErrors(newErrors);

        if (departmentError || departmentNameError || !isFormValid(newErrors)) {
            const firstErrorField = getFirstErrorField({
                ...newErrors,
                department: departmentError,
                departmentName: departmentNameError
            });
            if (firstErrorField) {
                const element = document.getElementById(firstErrorField);
                element?.focus();
            }
            return;
        }
        const payload = {
            request_id: "signup_organization",
            action: "organization_signup",
            data: await encryptResponse({
                user_full_name: formData.firstName + ' ' + formData.lastName,
                email_id: formData.email,
                mobile_no: formData.mobile,
                organization_name: formData.organizationName,
                password: formData.password,
                c_password: formData.confirmPassword,
                department: department === 'Others' ? departmentName : department,
                is_email_verified: isEmailVerified
            }, REQ_ENC_KEY)
        };

        try {
            type SignupResponse = { status: number;[key: string]: any };
            const response = await httpService.post<SignupResponse>(`${API_BASE_URL}/user/signUp`, payload);
            if (response.data.status === 200 && response.data.data) {
                const loginPayload = {
                    request_id: "login_req",
                    timestamp: new Date().toISOString(),
                    action: "organization_login",
                    data: await encryptResponse({
                        email_id: formData.email,
                        password: formData.password,
                        is_email_verified: isEmailVerified
                    }, REQ_ENC_KEY)
                };
                type LoginResponse = { status: number;[key: string]: any };
                const loginResponse = await httpService.post<LoginResponse>(`${API_BASE_URL}/user/login`, loginPayload);
                if (loginResponse.data.status === 200 && loginResponse.data.data) {
                    const decryptedLoginData = await decryptResponse(loginResponse.data.data, REQ_ENC_KEY);
                    const { payload, session_key } = (decryptedLoginData as any);
                    const decrypted = await decryptResponse<{
                        token: string;
                        refresh_token?: string;
                        userDetails: any;
                        user_process: any;
                        activePlanDetails: any;
                    }>(payload, session_key);
                    const { token, userDetails, user_process, activePlanDetails } = decrypted;
                    dispatch(setToken(token));
                    dispatch(setUserDetails({...userDetails, ...user_process, activePlanDetails}));
                    setTimeout(() => {
                        navigate('/home');
                    }, 1500);
                }
                setErrorMessage('');
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    confirmPassword: '',
                    organizationName: '',
                    password: '',
                    mobile: '',
                });
                setIsEmailVerified('');
                toast.success('Sign Up Successful!', { position: 'top-right', autoClose: 1500 });
            } else if (response.data.status === 409) {
                setErrorMessage('Email or mobile number already exists.');
            } else {
                setErrorMessage('Signup failed. Please try again.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            // setLoading(false);
        }
        console.log('Signup attempt:', formData);
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleOtpPopupClose = () => {
        setIsOtpPopupOpen(false);
        setOtp('');
        setOtpError('');
    };

    const sendOtp = async () => {
        if (!formData.email || errors.email) {
            setErrorMessage('Please enter a valid email address to send OTP.');
            return;
        }
        
        setIsLoading(true); // Start loading
        
        const payload = {
            request_id: 'signup_otp',
            action: "send_otp",
            data: await encryptResponse({
                email_id: formData.email,
                purpose: "signup"
            }, REQ_ENC_KEY)
        };
        try {
            const response = await httpService.post<any>(`${API_BASE_URL}/user/sendOTP`, payload);
            if (response.data.status === 200) {
                setOtpSent(true);
                setErrorMessage('');
                setIsOtpPopupOpen(true);
                toast.success('OTP sent successfully!', { position: 'top-right', autoClose: 1500 });
            } else {
                const errorMessage = response.data?.error?.errorDetails || 'Failed to send OTP. Please try again.';
                setErrorMessage(errorMessage);
                toast.error(errorMessage, { position: 'top-right', autoClose: 1500 });
            }
        } catch (error) {
            console.log(error);
            setErrorMessage('An error occurred while sending OTP.');
        } finally {
            setIsLoading(false); // Stop loading
        }
    };

    const verifyOtp = async () => {
        if (otp.length !== 6) {
            setOtpError('Please enter the 6-digit OTP.');
            return;
        }
        const payload = {
            request_id: 'test_otp',
            action: "verify_otp",
            data: await encryptResponse({
                email_id: formData.email,
                otp: otp
            }, REQ_ENC_KEY)
        };
        try {
            const response = await httpService.post<VerifyOtpResponse>(`${API_BASE_URL}/user/verifyOTP`, payload);
            if (response.data.status === 200 && response.data.data) {
                const decryptedData = await decryptResponse(response.data.data, REQ_ENC_KEY);
                const is_email_verified = (decryptedData as any).is_email_verified;
                console.log('OTP verified successfully:', response.data);
                setEmailVerified(true);
                setOtpError('');
                setIsOtpPopupOpen(false);
                setIsEmailVerified(is_email_verified);
                toast.success('Email verified successfully!', { position: 'top-right', autoClose: 1500 });
            } else {
                setOtpError('Invalid OTP. Please try again.');
            }
        } catch (error) {
            console.log(error);
            setOtpError('An error occurred while verifying OTP.');
        }
    };

    return (
        <Box sx={{ overflowY: 'auto', height: '100vh' }}>
            <Container component="main" maxWidth="sm">
            
            {/* Full Screen Loader */}
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
                open={isLoading}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Sending OTP...
                    </Typography>
                </Box>
            </Backdrop>

            <Box
                sx={{
                    position: 'fixed',
                    top: 14,
                    left: 24,
                    zIndex: 1000,
                }}
            >
                <img
                    src="/images/sampark-logo.png"
                    alt="Company Logo"
                    style={{ height: '90px', width: 'auto' }}
                />
            </Box>
            <Box
                sx={{
                    marginTop: 4,
                    marginBottom: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <Typography component="h1" variant="h4" gutterBottom>
                            Create Account
                        </Typography>
                        {errorMessage !== '' && <Typography style={{ color: 'red' }} component="text" variant="body1" gutterBottom>
                            {errorMessage}
                        </Typography>}
                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Tooltip title="Enter Admin first name" arrow>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="firstName"
                                        label="First Name"
                                        name="firstName"
                                        autoComplete="off"
                                        autoFocus
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.firstName && !!errors.firstName}
                                        helperText={touched.firstName && errors.firstName}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: formData.firstName && !errors.firstName ? (
                                                    <InputAdornment position="end">
                                                        <CheckCircle sx={{ color: 'green' }} />
                                                    </InputAdornment>
                                                ) : null,
                                            },
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="Enter Admin last name" arrow>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="lastName"
                                        label="Last Name"
                                        name="lastName"
                                        autoComplete="off"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.lastName && !!errors.lastName}
                                        helperText={touched.lastName && errors.lastName}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: formData.lastName && !errors.lastName ? (
                                                    <InputAdornment position="end">
                                                        <CheckCircle sx={{ color: 'green' }} />
                                                    </InputAdornment>
                                                ) : null,
                                            },
                                        }}
                                    />
                                </Tooltip>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Enter Admin email address - this will be used for account verification" arrow>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        label="Email Address"
                                        name="email"
                                        autoComplete="off"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && !!errors.email}
                                        helperText={touched.email && errors.email}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Email />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: emailVerified ? (
                                                    <InputAdornment position="end">
                                                        <CheckCircle sx={{ color: 'green' }} />
                                                    </InputAdornment>
                                                ) : null,
                                            },
                                        }}
                                    />
                                </Tooltip>
                                {!otpSent && !emailVerified && formData.email && !errors.email && !validateEmail(formData.email) && (
                                    <Tooltip title="Click to send OTP to your email for verification" arrow>
                                        <Button
                                            variant="contained"
                                            onClick={sendOtp}
                                            disabled={isLoading}
                                            sx={{ height: '40px', width: '180px' }}
                                        >
                                            {isLoading ? 'Sending...' : 'Verify Email'}
                                        </Button>
                                    </Tooltip>
                                )}
                            </Box>
                            <Tooltip title="Enter Admin mobile number (10 digits)" arrow>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="mobile"
                                    label="Mobile Number"
                                    name="mobile"
                                    autoComplete="off"
                                    type="number"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.mobile && !!errors.mobile}
                                    helperText={touched.mobile && errors.mobile}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Phone />
                                                </InputAdornment>
                                            ),
                                            endAdornment: formData.mobile && !errors.mobile && !validateMobileNumber(formData.mobile) ? (
                                                <InputAdornment position="end">
                                                    <CheckCircle sx={{ color: 'green' }} />
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                />
                            </Tooltip>

                            <Tooltip title="Enter your organization or company name" arrow>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="organizationName"
                                    label="Organization Name"
                                    name="organizationName"
                                    autoComplete="off"
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.organizationName && !!errors.organizationName}
                                    helperText={touched.organizationName && errors.organizationName}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Business />
                                                </InputAdornment>
                                            ),
                                            endAdornment: formData.organizationName && !errors.organizationName ? (
                                                <InputAdornment position="end">
                                                    <CheckCircle sx={{ color: 'green' }} />
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                />
                            </Tooltip>

                            <Tooltip title="Select your department (required)" arrow>
                                <TextField
                                    select
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="department"
                                    label="Department"
                                    name="department"
                                    value={department}
                                    onChange={handleDepartmentChange}
                                    error={touched.department && !!departmentError}
                                    helperText={touched.department && departmentError}
                                    slotProps={{
                                        select: {
                                            native: true
                                        },

                                    }}
                                >
                                    <option value="IT">IT</option>
                                    <option value="HR">HR</option>
                                    <option value="Finance">Finance</option>
                                    <option value="Marketing">Marketing</option>
                                    <option value="Sales">Sales</option>
                                    <option value="Operations">Operations</option>
                                    <option value="Legal">Legal</option>
                                    <option value="Administration">Administration</option>
                                    <option value="Research & Development">Research & Development</option>
                                    <option value="Customer Support">Customer Support</option>
                                    <option value="Quality Assurance">Quality Assurance</option>
                                    <option value="Others">Others</option>
                                </TextField>
                            </Tooltip>

                            {department === 'Others' && (
                                <Tooltip title="Enter your department name" arrow>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="departmentName"
                                        label="Department Name"
                                        name="departmentName"
                                        value={departmentName}
                                        onChange={handleDepartmentNameChange}
                                        onBlur={handleDepartmentNameBlur}
                                        error={touched.departmentName && !!departmentNameError}
                                        helperText={touched.departmentName && departmentNameError}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Business />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: departmentName && !departmentNameError ? (
                                                    <InputAdornment position="end">
                                                        <CheckCircle sx={{ color: 'green' }} />
                                                    </InputAdornment>
                                                ) : null,
                                            },
                                        }}
                                    />
                                </Tooltip>
                            )}

                            <Tooltip title="Password must be at least 8 characters with uppercase, lowercase, number and special character" arrow>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    autoComplete="off"
                                    value={formData.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.password && !!errors.password}
                                    helperText={touched.password && errors.password}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {formData.password && !errors.password && !validatePasswordStrong(formData.password) && (
                                                        <CheckCircle sx={{ color: 'green', mr: 1 }} />
                                                    )}
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Tooltip>

                            <Tooltip title="Re-enter your password to confirm" arrow>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    autoComplete="off"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.confirmPassword && !!errors.confirmPassword}
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {formData.confirmPassword && !errors.confirmPassword && !validateConfirmPassword(formData.password, formData.confirmPassword) && (
                                                        <CheckCircle sx={{ color: 'green', mr: 1 }} />
                                                    )}
                                                    <IconButton
                                                        aria-label="toggle confirm password visibility"
                                                        onClick={handleClickShowConfirmPassword}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Tooltip>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={
                                    !formData.firstName ||
                                    !formData.lastName ||
                                    !formData.email ||
                                    !formData.mobile ||
                                    !formData.organizationName ||
                                    !formData.password ||
                                    !formData.confirmPassword ||
                                    !department ||
                                    (department === 'Others' && !departmentName) ||
                                    !emailVerified ||
                                    Object.values(errors).some(error => error !== '') ||
                                    departmentError !== '' ||
                                    departmentNameError !== ''
                                }
                                sx={{ mt: 3, mb: 2, py: 1.5 }}
                            >
                                Create Account
                            </Button>

                            <Box sx={{ textAlign: 'center' }}>
                                Already have an account?
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate('/login')}
                                    sx={{ textDecoration: 'none', marginLeft: '5px' }}
                                >
                                    Sign In
                                </Link>
                            </Box>

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography variant="body2" sx={{ color: '#9aa4b2', fontSize: '0.875rem' }}>
                                    By creating an account, you agree to our{' '}
                                    <Link 
                                        component="button" 
                                        variant="body2" 
                                        onClick={() => navigate('/terms')}
                                        sx={{ 
                                            textDecoration: 'none', 
                                            color: '#0b4f88',
                                            fontSize: '0.875rem',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Terms and Conditions
                                    </Link>
                                    {' '}and{' '}
                                    <Link 
                                        component="button" 
                                        variant="body2" 
                                        onClick={() => navigate('/privacy-policy')}
                                        sx={{ 
                                            textDecoration: 'none', 
                                            color: '#0b4f88',
                                            fontSize: '0.875rem',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Privacy Policy
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Dialog
                open={isOtpPopupOpen}
                onClose={handleOtpPopupClose}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            width: '500px',
                            maxWidth: '90vw'
                        }
                    }
                }}
            >
                <DialogTitle>Verify Email</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Enter the 6-digit OTP sent to <strong>{formData.email}</strong>.
                    </Typography>
                    <OtpInput
                        id="otp"
                        value={otp}
                        onChange={(value) => {
                            setOtp(value);
                            if (otpError) {
                                setOtpError('');
                            }
                        }}
                        error={!!otpError}
                        helperText={otpError}
                        autoFocus={isOtpPopupOpen}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleOtpPopupClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={verifyOtp} color="primary" disabled={otp.length !== 6}>
                        Verify OTP
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
        </Box>
    );
};

export default Signup;

type SendOtpResponse = { status: number; message?: string };
type VerifyOtpResponse = { status: number; message?: string; data: any };
